import type { ThreatLevelId } from "../core/MetaState.ts";
import { THREAT_LEVEL_IDS } from "../core/MetaState.ts";
import type { WaveManager } from "./WaveManager.ts";

/**
 * ThreatEffects — the THREAT GAMEPLAY CONTRACT (chain step A; owner-approved
 * 2026-08-16/17, implemented 2026-08-18). The five threat levels stop being a
 * label and become five real difficulty populations along exactly TWO axes:
 *
 *   level        enemy HP   enemy speed
 *   sandbox        0.85        0.95     — a true practice mode
 *   containment    1.00        1.00     — THE NEUTRAL ANCHOR (see below)
 *   lockdown       1.15        1.05
 *   overrun        1.30        1.10
 *   fallout        1.45        1.15    — the extreme on both axes; "hardcore"
 *
 * CONTRACT RAILS (owner, non-negotiable):
 * - CONTAINMENT IS THE 1.00 ANCHOR: with its profile (or with no profile
 *   applied at all — the WaveManager default) the simulation is BYTE-IDENTICAL
 *   to the pre-contract game. Golden masters run on that anchor and must not
 *   move. IEEE-754 guarantees `x * 1.0 === x`, so the multiply below is exact.
 * - NO reward multipliers, NO score normalisation, NO extra RNG axes, NO
 *   economy penalty. Kill points come from enemy data; a harder run scores
 *   the same per kill and simply ends sooner. Scores are therefore only ever
 *   compared WITHIN a level (five local top-10s, five D1 populations, five
 *   Game Center boards) — separation, never normalisation.
 *
 * Mirrors the OperatorEffects pattern (D-322): magnitudes live in this
 * mutable code-fallback object, byte-identically overridable from
 * `public/data/threat-levels.json` via {@link applyThreatLevelsData} (data is
 * the source of truth; the code object is the no-file fallback), and
 * {@link applyThreatLevelToSystems} is called ONCE at run start — BEFORE
 * `applyOperatorToSystems`, so the run's base difficulty is set before any
 * operator/module augment reads or changes systems.
 */
export interface ThreatProfile {
  /** Multiplies every spawned enemy's HP (applied before rounding). */
  enemyHpMult: number;
  /** Multiplies every spawned enemy's speed (before the 2.5× physics cap). */
  enemySpeedMult: number;
}

export const THREAT_PROFILES: Record<ThreatLevelId, ThreatProfile> = {
  sandbox: { enemyHpMult: 0.85, enemySpeedMult: 0.95 },
  containment: { enemyHpMult: 1.0, enemySpeedMult: 1.0 },
  lockdown: { enemyHpMult: 1.15, enemySpeedMult: 1.05 },
  overrun: { enemyHpMult: 1.3, enemySpeedMult: 1.1 },
  fallout: { enemyHpMult: 1.45, enemySpeedMult: 1.15 },
};

/** Raw shape of `public/data/threat-levels.json` (pre-validation). */
export interface ThreatLevelsDataFile {
  profiles?: Partial<Record<string, Partial<ThreatProfile>>>;
}

/**
 * Apply the optional `threat-levels.json` override onto
 * {@link THREAT_PROFILES} in place — same tolerant pattern as
 * `applyOperatorsData`. Validation is strict where it matters: only the five
 * canonical level ids are accepted (an unknown key cannot smuggle a sixth
 * population in), and only finite positive numbers land (a malformed value
 * cannot zero out an axis).
 */
export function applyThreatLevelsData(
  data?: ThreatLevelsDataFile | null
): void {
  if (!data?.profiles) return;
  for (const [level, profile] of Object.entries(data.profiles)) {
    if (!(THREAT_LEVEL_IDS as readonly string[]).includes(level) || !profile) {
      continue;
    }
    const target = THREAT_PROFILES[level as ThreatLevelId];
    for (const axis of ["enemyHpMult", "enemySpeedMult"] as const) {
      const v = profile[axis];
      if (typeof v === "number" && Number.isFinite(v) && v > 0) {
        target[axis] = v;
      }
    }
  }
}

export interface ThreatContext {
  waveManager: WaveManager;
}

export interface AppliedThreatEffect {
  level: ThreatLevelId;
  profile: ThreatProfile;
}

/**
 * Apply the run's threat profile at run start — called from `startRun()`
 * BEFORE `applyOperatorToSystems` (base difficulty first, augments after).
 * Every run sets its profile explicitly, so a restart on a different level
 * can never inherit the previous run's difficulty.
 */
export function applyThreatLevelToSystems(
  level: ThreatLevelId,
  ctx: ThreatContext
): AppliedThreatEffect {
  const profile = THREAT_PROFILES[level];
  ctx.waveManager.setThreatProfile(profile);
  return { level, profile };
}
